import React, { useEffect, useState } from "react";
import { PageHeader, Card } from "@/components/Primitives";
import { api } from "@/lib/api";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Loader2, Clock } from "lucide-react";

const MAP = {
  operational: { label: "Operational", color: "#5a9a6a", bg: "#8FB996", icon: CheckCircle2 },
  degraded: { label: "Degraded", color: "#a9791f", bg: "#C6A45C", icon: AlertTriangle },
  offline: { label: "Offline", color: "#b06b6b", bg: "#C99FA0", icon: XCircle },
};

// Fallback framework (used only if the live endpoint is unavailable).
const MOCK = {
  overall: "operational", checked_at: new Date().toISOString(), last_incident: null, planned_maintenance: null,
  groups: [
    { name: "Platform", items: [{ label: "Website", status: "operational" }, { label: "API", status: "operational" }, { label: "Authentication", status: "operational" }] },
    { name: "Financial Services", items: [{ label: "Financial Inbox", status: "operational" }, { label: "Document Processing", status: "operational" }, { label: "Cash Flow Engine", status: "operational" }] },
    { name: "Email Services", items: [{ label: "Resend", status: "operational" }, { label: "Mailgun", status: "operational" }, { label: "Incoming Email Processing", status: "operational" }] },
    { name: "AI Services", items: [{ label: "AI Assistant", status: "operational" }, { label: "OCR", status: "operational" }, { label: "Transaction Categorisation", status: "operational" }] },
    { name: "Database", items: [{ label: "Database connection", status: "operational" }, { label: "File storage", status: "operational" }] },
  ],
};

function Pill({ status }) {
  const m = MAP[status] || MAP.operational;
  const Icon = m.icon;
  return (
    <span data-testid={`status-pill-${status}`} className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1"
      style={{ background: `${m.bg}22`, color: m.color, border: `1px solid ${m.bg}55` }}>
      <Icon size={13} /> {m.label}
    </span>
  );
}

export default function Status() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/meta/status").then((r) => setData(r.data)).catch(() => setData(MOCK)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const d = data || MOCK;
  const overall = MAP[d.overall] || MAP.operational;
  const checked = d.checked_at ? new Date(d.checked_at).toLocaleString() : "—";

  return (
    <div data-testid="status-page">
      <PageHeader title="Service Status" subtitle="Live health of Finetomi services." />

      <Card data-testid="status-overall" className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
        style={{ borderColor: `${overall.bg}66` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: `${overall.bg}22` }}>
            <overall.icon size={22} style={{ color: overall.color }} />
          </div>
          <div>
            <div className="font-display text-xl text-[#2C2A28]">
              {d.overall === "operational" ? "All systems operational" : d.overall === "degraded" ? "Some services degraded" : "Service disruption"}
            </div>
            <div className="text-xs text-[#A99F90] flex items-center gap-1.5 mt-0.5"><Clock size={12} /> Last checked {checked}</div>
          </div>
        </div>
        <button data-testid="status-refresh" onClick={load} disabled={loading}
          className="inline-flex items-center gap-2 text-sm border border-[#D9CDBA] text-[#6b6256] bg-white/60 rounded-xl px-4 py-2.5 hover:border-[#C6A45C] hover:bg-white transition-all disabled:opacity-60">
          {loading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Refresh
        </button>
      </Card>

      <div className="space-y-5">
        {d.groups.map((g) => (
          <Card key={g.name} data-testid={`status-group-${g.name.replace(/\s+/g, "-").toLowerCase()}`}>
            <h3 className="font-display text-lg text-[#2C2A28] mb-4">{g.name}</h3>
            <div className="divide-y divide-[#F0E9DD]">
              {g.items.map((it) => (
                <div key={it.label} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <span className="text-sm text-[#2C2A28]">{it.label}</span>
                    {it.note && <div className="text-xs text-[#A99F90]">{it.note}</div>}
                  </div>
                  <Pill status={it.status} />
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
        <Card data-testid="status-incident">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A7F70] mb-2">Last incident</div>
          <div className="text-sm text-[#6b6256]">{d.last_incident || "No incidents reported."}</div>
        </Card>
        <Card data-testid="status-maintenance">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A7F70] mb-2">Planned maintenance</div>
          <div className="text-sm text-[#6b6256]">{d.planned_maintenance || "None scheduled."}</div>
        </Card>
      </div>
    </div>
  );
}
